import { useEffect, useState } from "react";
import axios from "axios";
import useTranslator from "../../hooks/useTranslator";
import {
  Avatar,
  Button,
  Checkbox,
  Icon,
  InfoList,
  PublicPage,
  PublicPageStatus,
} from "../../components/design";
import "./BeneficiaryInsurancePayment.css";

const STATUS = {
  LOADING: "loading",
  REVIEW: "review",
  REDIRECTING: "redirecting",
  SUCCESS: "success",
  FAILURE: "failure",
  ERROR: "error",
};

const BeneficiaryInsurancePayment = () => {
  const { _t, i18n } = useTranslator();

  const [status, setStatus] = useState(STATUS.LOADING);
  const [errorMessage, setErrorMessage] = useState("");
  const [token, setToken] = useState("");
  const [details, setDetails] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [payLoading, setPayLoading] = useState(false);

  useEffect(() => {
    i18n.changeLanguage(
      localStorage.getItem("i18nextLng")?.toLowerCase() || "es"
    );
  }, []);

  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted && status === STATUS.REDIRECTING) {
        setPayLoading(false);
        setStatus(STATUS.ERROR);
        setErrorMessage(_t("invalidOrExpiredPaymentLink"));
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, [status]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");
    const paymentStatus = params.get("status");

    window.history.replaceState({}, "", window.location.pathname);

    if (paymentStatus === "success") {
      setStatus(STATUS.SUCCESS);
      return;
    }

    if (paymentStatus === "failure") {
      setStatus(STATUS.FAILURE);
      return;
    }

    if (!urlToken) {
      setStatus(STATUS.ERROR);
      setErrorMessage(_t("invalidOrExpiredPaymentLink"));
      return;
    }

    setToken(urlToken);
    fetchPaymentDetails(urlToken);
  }, []);

  const fetchPaymentDetails = async (token) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/insurance/beneficiary/payment-details`,
        {
          params: { token },
          headers: {
            "Accept-Language": localStorage.getItem("i18nextLng") || "en",
          },
        }
      );

      setDetails(res.data?.success_res || null);
      setStatus(STATUS.REVIEW);
    } catch {
      setStatus(STATUS.ERROR);
      setErrorMessage(_t("paymentLinkIsInvalidOrExpired"));
    }
  };

  const handleContinueToPayment = () => {
    if (!confirmed || payLoading) return;
    setPayLoading(true);
    setStatus(STATUS.REDIRECTING);

    axios
      .post(
        `${process.env.REACT_APP_SERVER_URL}/insurance/beneficiary/initiate-payment`,
        { token },
        {
          headers: {
            "Accept-Language": localStorage.getItem("i18nextLng") || "en",
          },
        }
      )
      .then((res) => {
        const paymentUrl = res.data?.success_res?.paymentUrl;

        if (!paymentUrl) {
          setStatus(STATUS.ERROR);
          setErrorMessage(_t("paymentLinkNotReceived"));
          return;
        }

        window.location.href = paymentUrl;
      })
      .catch(() => {
        setStatus(STATUS.ERROR);
        setErrorMessage(_t("paymentLinkIsInvalidOrExpired"));
      })
      .finally(() => {
        setPayLoading(false);
      });
  };

  if (status === STATUS.LOADING || status === STATUS.REDIRECTING) {
    return (
      <PublicPage width="sm">
        <PublicPageStatus
          title={
            status === STATUS.REDIRECTING
              ? _t("redirectingToPayment")
              : _t("verifyingYourPaymentLink")
          }
          description={_t(
            "pleaseWaitWhileWeSecurelyProcessYourPayment",
            "Please wait while we securely process your payment."
          )}
        />
      </PublicPage>
    );
  }

  if (status === STATUS.REVIEW) {
    const payerName =
      [details?.beneficiary?.first_name, details?.beneficiary?.last_name]
        .filter(Boolean)
        .join(" ") || "—";
    const amount =
      details?.insurance?.total_amount != null
        ? `€${details.insurance.total_amount}`
        : "—";
    const propertyAddress = details?.property?.address || "—";
    const monthlyRent =
      details?.insurance?.monthly_rent != null
        ? `€${details.insurance.monthly_rent}/${_t("perMonth")}`
        : "—";
    const coverageMonths = Number(details?.insurance?.coverage_months) || 12;
    const coverageDuration = coverageMonths
      ? `${coverageMonths} ${_t("months")}`
      : "—";
    const insuranceProductLabel =
      coverageMonths === 12
        ? _t(
            "annualRentProtectionInsurance",
            "Annual Rent Protection Insurance"
          )
        : `${coverageMonths} ${_t("months")} - ${_t(
            "rentProtectionInsurance",
            "Rent Protection Insurance"
          )}`;
    const startDate = details?.insurance?.start_date || "—";
    const policyStartDate = details?.insurance?.policy_start_date || "—";
    const tenants = (details?.selected_tenants || []).map((tenant) => ({
      name: [tenant.first_name, tenant.last_name].filter(Boolean).join(" "),
    }));

    const insurancePolicyUrl = details?.prefilled_insurance_url
      ? `${details.prefilled_insurance_url}`
      : null;

    return (
      <PublicPage
        width="md"
        title={_t("reviewBeforePayment")}
        description={_t(
          "reviewBeforePaymentSubtitle",
          "Please confirm that all the information is correct before proceeding with the rent protection insurance payment."
        )}
        footer={_t(
          "insurancePaymentLinkSingleUseExpiryNote",
          "This link can only be used once and expires in 48 hours."
        )}
      >
        <div className="bip">
          <div className="bip__block">
            <h2 className="bip__heading">{_t("youAreThePolicyholder")}</h2>
            <InfoList
              items={[
                { label: _t("payingForThisInsurance"), value: payerName },
              ]}
            />
          </div>

          <div className="bip__block">
            <h2 className="bip__heading">{_t("amountToPay")}</h2>
            <InfoList
              items={[{ label: insuranceProductLabel, value: amount }]}
            />
          </div>

          <div className="bip__block">
            <h2 className="bip__heading">
              {_t("insuranceReviewPropertyLabel")}
            </h2>
            <InfoList
              items={[{ label: _t("address"), value: propertyAddress }]}
            />
          </div>

          <div className="bip__block">
            <h2 className="bip__heading">{_t("rentalTerms")}</h2>
            <InfoList
              items={[
                {
                  label: _t("monthlyRent", "Monthly Rent"),
                  value: monthlyRent,
                },
                { label: _t("insuranceCoverage"), value: coverageDuration },
                { label: _t("startDate", "Start Date"), value: startDate },
                { label: _t("policyStartDate"), value: policyStartDate },
              ]}
            />
          </div>

          <div className="bip__block">
            <h2 className="bip__heading">{_t("tenants")}</h2>
            {tenants.length ? (
              <ul className="bip__people">
                {tenants.map((tenant, idx) => (
                  <li key={idx}>
                    <Avatar name={tenant.name} />
                    <span>{tenant.name}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="bip__empty">—</p>
            )}
          </div>

          {insurancePolicyUrl && (
            <div className="bip__block">
              <h2 className="bip__heading">
                {_t("relevantDocuments", "Relevant Documents")}
              </h2>
              <div className="bip__docs">
                {insurancePolicyUrl && (
                  <a
                    href={insurancePolicyUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="bip__doc"
                  >
                    <Icon name="shieldCheck" />
                    <span>{_t("insurancePolicy", "Insurance Policy")}</span>
                    <Icon name="download" />
                  </a>
                )}
              </div>
            </div>
          )}

          <Checkbox
            id="bip-confirm"
            checked={confirmed}
            onChange={setConfirmed}
            label={_t(
              "confirmDetailsCheckboxLabel",
              "I have reviewed the property, the tenants, and the rental terms, and I confirm that all the information is correct."
            )}
          />
          <Button
            size="lg"
            block
            disabled={!confirmed || payLoading}
            onClick={handleContinueToPayment}
          >
            {_t("continueToPayment", "Continue to Payment")}
          </Button>
        </div>
      </PublicPage>
    );
  }

  if (status === STATUS.SUCCESS) {
    return (
      <PublicPage width="sm">
        <PublicPageStatus
          tone="success"
          title={_t("paymentSuccessful")}
          description={_t(
            "yourPaymentHasBeenCompletedSuccessfully",
            "Your payment has been completed successfully."
          )}
        />
      </PublicPage>
    );
  }

  if (status === STATUS.FAILURE) {
    return (
      <PublicPage width="sm">
        <PublicPageStatus
          tone="danger"
          title={_t("paymentNotCompleted")}
          description={_t(
            "yourPaymentCouldNotBeCompletedPleaseTryAgain",
            "Your payment could not be completed. Please try again."
          )}
        />
      </PublicPage>
    );
  }

  // ERROR state
  return (
    <PublicPage width="sm">
      <PublicPageStatus
        tone="danger"
        title={_t("unableToProcessPayment")}
        description={errorMessage}
      />
    </PublicPage>
  );
};

export default BeneficiaryInsurancePayment;
